import { SubscriptionItem } from '@/api';
import { PaymentType } from './org-payment-method';

export const BASE_TIERS = ['trim', 'regular', 'standard'];

export const WATER_ENTITY_BILLING_TIERS = [
	{ value: 'standard', text: 'Standard' },
	{ value: 'premium', text: 'Premium' },
];

export const hasPartnerPlan = (plan: OrdwayPlan): boolean =>
	plan.levno_plan_type === 'directWithPartner' ||
	plan.levno_plan_type === 'directWithPartnerRebate' ||
	plan.levno_plan_type === 'directWithPartnerPaying';

export const getDirectPlans = (
	plans: OrdwayPlan[],
	productType: string,
	supplier: string,
	isBillingAdmin: boolean,
): OrdwayPlan[] => {
	const directPlans = plans.filter(
		(plan: OrdwayPlan) =>
			plan.levno_product_type === productType &&
			plan.levno_plan_type !== 'partner' &&
			(plan.levno_required_partner === supplier ||
				plan.levno_required_partner === '' ||
				plan.levno_required_partner === 'n/a'),
	);
	if (isBillingAdmin === false) {
		return directPlans.filter((plan: OrdwayPlan) => plan.status !== 'Inactive');
	} else {
		return directPlans;
	}
};

export const getPartnerPlans = (
	plans: OrdwayPlan[],
	productType: string,
	supplier: string,
	isBillingAdmin: boolean,
): OrdwayPlan[] => {
	const partnerPlans: OrdwayPlan[] = plans.filter(
		(plan: OrdwayPlan) =>
			plan.levno_product_type === productType &&
			plan.levno_plan_type === 'partner' &&
			(plan.levno_required_partner === supplier || plan.levno_required_partner === 'n/a'),
	);
	if (isBillingAdmin === false) {
		return partnerPlans.filter((plan: OrdwayPlan) => plan.status !== 'Inactive');
	} else {
		return partnerPlans;
	}
};

export const formatSubscriptionStatus = (subscriptionStatus: string): string => {
	if (subscriptionStatus !== '') {
		return subscriptionStatus;
	}
	return 'Not yet in Ordway';
};

export const getProductBillingAttributes = (
	productOrdwayAttribute: ProductOrdwayAttributes | null,
	partnerProductOrdwayAttribute: ProductOrdwayAttributes | null,
	subscriptionItem: SubscriptionItem,
): {
	ordwayBilling: {
		productPlan: { title: string; value: string };
		partnerPlan: { title: string; value: string };
		productTier: { title: string; value: string };
		subscriptionStatus: { title: string; value: string };
	};
	legacyBilling: {
		productTier: { title: string; value: string };
	};
	trialStatus: {
		trialPlan: { title: string; value: string };
		trialEnds: { title: string; value: string };
	};
} => {
	const ordwayBilling = {
		productPlan: { title: 'Product Plan', value: '' },
		partnerPlan: { title: 'Partner Plan', value: '' },
		productTier: { title: 'Product Tier', value: '' },
		subscriptionStatus: { title: 'Subscription Status', value: '' },
	};
	if (productOrdwayAttribute !== null) {
		ordwayBilling.productPlan.value = productOrdwayAttribute.ordwayPlanName;
		ordwayBilling.productTier.value = formatProductTier(
			productOrdwayAttribute.ordwayPlanProductTier,
		);
		ordwayBilling.subscriptionStatus.value = formatSubscriptionStatus(
			productOrdwayAttribute.ordwaySubscriptionStatus,
		);
	}
	if (partnerProductOrdwayAttribute !== null) {
		ordwayBilling.partnerPlan.value = partnerProductOrdwayAttribute.ordwayPlanName;
	} else {
		ordwayBilling.partnerPlan.value = 'No Partner Plan';
	}
	const legacyBilling = {
		productTier: {
			title: 'Billed Tier',
			value: formatProductTier(subscriptionItem.plan ?? 'N/A'),
		},
	};
	const trialStatus = {
		trialPlan: {
			title: 'Trial Tier',
			value: formatProductTier(subscriptionItem.trialPlan || 'No Trial'),
		},
		trialEnds: {
			title: 'Trial Ends',
			value: subscriptionItem.trialPlan
				? subscriptionItem.trialEndsAt
					? subscriptionItem.trialEndsAt
					: ''
				: '',
		},
	};

	return {
		ordwayBilling,
		legacyBilling,
		trialStatus,
	};
};

export const formatProductTierAttribute = (
	productOrdwayAttribute: ProductOrdwayAttributes | null,
) => {
	if (productOrdwayAttribute !== null) {
		const productTier: string = productOrdwayAttribute.ordwayPlanProductTier;
		return formatProductTier(productTier);
	}
	return '';
};

export const formatProductTier = (productTier: string) => {
	if (productTier !== 'n/a') {
		return productTier[0].toUpperCase() + productTier.substr(1);
	} else {
		return 'N/A';
	}
};

export interface OrdwayCharge {
	id: string;
	list_price: string;
	name: string;
	product_id: string;
	type: string;
	billing_period: string;
	pricing_model: string;
	tiers: OrdwayChargeTier[];
}

export interface OrdwayChargeTier {
	tier: number;
	starting_unit: string;
	ending_unit: string;
	price: string;
	type: string;
}

export interface FormattedOrdwayCharge {
	name: string;
	price: string;
	frequency: string;
}

export interface OrdwayPlan {
	id: string;
	levno_required_partner: string;
	levno_plan_type: string;
	levno_product_type: string;
	levno_product_tier: string;
	name: string;
	status: string;
	charges: OrdwayCharge[];
}

export interface ProductOrdwayAttributes {
	id: number;
	orgId: number;
	productId: number;
	ordwayPlanId: string;
	ordwayPlanName: string;
	ordwayPlanProductTier: string;
	ordwaySubscriptionId: string;
	ordwaySubscriptionStatus: string;
}

export interface OrdwaySubscriptionParams {
	orgId: number;
	productId: number;
	siteId: number;
	charges: OrdwayCharge[];
}

export interface OrdwayDraftSubscriptionUpdateParams extends OrdwaySubscriptionParams {
	newOrdwayPlanId: string;
}

export interface OrdwayPaymentInfoParams {
	type: PaymentType;
	billingApproved: boolean;
	paymentMethodActive: boolean;
	accountName?: string;
	accountNumber?: string;
}

export interface ProductOrdwayAttributesParams {
	orgId: number;
	productId: number;
	productTier: string;
	ordwayPlanId: string;
	ordwayPlanName: string;
	ordwaySubscriptionId?: string;
	ordwaySubscriptionStatus?: string;
	ordwayPartnerPlanId?: string;
	ordwayPartnerPlanName?: string;
	requiredPartner?: string;
}

export interface UpdateOrdwayPlansParams {
	productId: number;
	ordwayDirectPlanId: string;
	ordwayDirectPlanName: string;
	ordwayDirectPlanProductTier: string;
	partnerId?: number;
	ordwayPartnerPlanId?: string;
	ordwayPartnerPlanName?: string;
}
